import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, HelpCircle, Phone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { categories } from '@/utils/categories';

export const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleCategoryClick = (categoryValue, subcategoryValue = null) => {
    const params = new URLSearchParams();
    params.set('category', categoryValue);
    if (subcategoryValue) {
      params.set('subcategory', subcategoryValue);
    }
    navigate(`/artists?${params.toString()}`);
    setMegaMenuOpen(false);
    setMobileMenuOpen(false);
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center space-x-2" data-testid="home-link">
            <div className="text-2xl font-bold gradient-text">BOOK A STAR</div>
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            <div
              className="relative"
              onMouseEnter={() => setMegaMenuOpen(true)}
              onMouseLeave={() => setMegaMenuOpen(false)}
            >
              <button
                className="text-gray-700 hover:text-purple-600 font-medium transition-colors"
                data-testid="categories-menu-button"
              >
                Categories
              </button>

              {megaMenuOpen && (
                <div className="absolute left-0 top-full mt-2 w-screen max-w-4xl bg-white rounded-2xl shadow-2xl border border-gray-100 p-8" data-testid="mega-menu">
                  <div className="grid grid-cols-3 gap-8">
                    {Object.values(categories).map((category) => (
                      <div key={category.value} className="space-y-3">
                        <button
                          onClick={() => handleCategoryClick(category.value)}
                          className="text-lg font-semibold text-gray-900 hover:text-purple-600 transition-colors"
                          data-testid={`category-${category.value}`}
                        >
                          {category.name}
                        </button>
                        {category.subcategories.length > 0 && (
                          <div className="space-y-2">
                            {category.subcategories.map((sub) => (
                              <button
                                key={sub.value}
                                onClick={() => handleCategoryClick(category.value, sub.value)}
                                className="block text-sm text-gray-600 hover:text-purple-600 transition-colors"
                                data-testid={`subcategory-${sub.value}`}
                              >
                                {sub.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/artists"
              className="text-gray-700 hover:text-purple-600 font-medium transition-colors"
              data-testid="browse-artists-link"
            >
              Browse Artists
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="font-medium" data-testid="help-menu-button">
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Help
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <div className="px-3 py-2 text-sm font-semibold text-gray-900">Contact Us</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <a href="tel:9560890557" className="cursor-pointer flex items-center" data-testid="help-phone">
                    <Phone className="w-4 h-4 mr-2" />
                    +91 9560890557
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a href="mailto:yugantarsameer@gmail.com" className="cursor-pointer flex items-center" data-testid="help-email">
                    <Mail className="w-4 h-4 mr-2" />
                    yugantarsameer@gmail.com
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="rounded-full" data-testid="user-menu-button">
                    <User className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link
                      to={user.role === 'customer' ? '/dashboard/customer' : '/dashboard/artist'}
                      className="cursor-pointer"
                      data-testid="dashboard-link"
                    >
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer" data-testid="logout-button">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center space-x-4">
                <Link to="/login">
                  <Button variant="ghost" data-testid="login-button">Login</Button>
                </Link>
                <Link to="/register">
                  <Button className="rounded-full bg-purple-600 hover:bg-purple-700" data-testid="register-button">
                    Get Started
                  </Button>
                </Link>
              </div>
            )}
          </div>

          <button
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-4" data-testid="mobile-menu">
            <Link
              to="/artists"
              className="block text-gray-700 hover:text-purple-600"
              onClick={() => setMobileMenuOpen(false)}
            >
              Browse Artists
            </Link>
            {user ? (
              <>
                <Link
                  to={user.role === 'customer' ? '/dashboard/customer' : '/dashboard/artist'}
                  className="block text-gray-700 hover:text-purple-600"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="block text-gray-700 hover:text-purple-600"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="block text-gray-700 hover:text-purple-600"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="block text-gray-700 hover:text-purple-600"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Register
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};
